/* Gleva · Direction B · gvb-chrome.js — header, Shop panel, menu drawer, search overlay and toast glue (ES module,
   loaded by sections/header.liquid). Impact's elements do the heavy lifting (<navigation-drawer>, <search-drawer>,
   <predictive-search>, <cart-drawer>); this file only adds the B behaviours from final-b2 (shared.js, flows.js):
   - desktop "Shop" panel [data-mega-btn] / [data-mega]: a disclosure (not a menu role). Click, Enter or Space toggles;
     with a mouse it opens after 120 ms of hover and closes 220 ms after the pointer leaves the button and the panel
     (a click right after hover opened it keeps it open). Esc closes it and refocuses "Shop"; a click outside, Tab past
     the panel or narrowing below 1024 px closes it. While open: .hdr.is-mega (hairline + 20% scrim, CSS)
   - menu "Search Gleva" pill: closes the menu, then opens the search overlay
   - "Try searching" chips and suggestions ([data-s-pick]) fill the field and run the predictive search
   - a persistent live region reads the result count ("4 products"); the typed term is marked in product names
   - links inside the menu drawer close it (same-page anchors such as #tiers)
   - kit links [data-gvb-tier] on the kit's own page select that tier (gvb-pdp.js `gvb:select-tier`) and scroll to it
   - toast "View bag" opens Impact's cart drawer (gvb.js builds and fills the toast)
   - deferred drawer images: the closed menu drawer, Shop panel and search overlay ([data-gvb-defer]) render their
     <img> with data-gvb-src / data-gvb-srcset (header.liquid, search-drawer.liquid); they get real src/srcset on the
     first hover, focus, touch or click of an opener, or when the drawer/panel opens (speed report #3) */

const $ = (s, r) => (r || document).querySelector(s);
const searchDrawer = () => document.getElementById('search-drawer');
const menuDrawer = () => document.getElementById('header-sidebar-menu');
const desk = window.matchMedia('(min-width:1024px)');

const runSearch = (term) => {
  const sd = searchDrawer();
  const input = sd && $('input[name="q"]', sd);
  if (!input) return false;
  input.value = term;
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.focus({ preventScroll: true });
  return true;
};

/* ---------- deferred images in closed drawers ---------- */
const hydrate = (scope) => {
  if (!scope || scope._gvbHydrated) return;
  scope._gvbHydrated = true;
  scope.querySelectorAll('img[data-gvb-src]').forEach((img) => {
    const ss = img.getAttribute('data-gvb-srcset');
    if (ss) { img.setAttribute('srcset', ss); img.removeAttribute('data-gvb-srcset'); }
    img.setAttribute('src', img.getAttribute('data-gvb-src'));
    img.removeAttribute('data-gvb-src');
  });
};
const deferTargetOf = (el) => {
  if (el.matches('[data-mega-btn]')) return document.getElementById(el.getAttribute('aria-controls'));
  if (el.matches('[data-gvb-open-search]')) return searchDrawer();
  const id = el.getAttribute('aria-controls');
  return id === 'header-sidebar-menu' || id === 'search-drawer' ? document.getElementById(id) : null;
};
const OPENERS = '[aria-controls="header-sidebar-menu"],[aria-controls="search-drawer"],[data-gvb-open-search],[data-mega-btn]';
['pointerover', 'focusin', 'touchstart', 'click'].forEach((type) => {
  document.addEventListener(type, (e) => {
    const t = e.target instanceof Element ? e.target.closest(OPENERS) : null;
    const d = t && deferTargetOf(t);
    if (d) hydrate(d.closest('[data-gvb-defer]') || d.querySelector('[data-gvb-defer]') || d);
  }, { capture: true, passive: true });
});
const watchDefer = () => {
  document.querySelectorAll('[data-gvb-defer]').forEach((el) => {
    if (el._gvbDeferWatch) return;
    el._gvbDeferWatch = true;
    const host = el.closest('navigation-drawer,search-drawer') || el;
    const isOpen = () => host.hasAttribute('open') || (el.hasAttribute('data-mega') && !el.hidden);
    if (isOpen()) { hydrate(el); return; }
    const mo = new MutationObserver(() => { if (isOpen()) { hydrate(el); mo.disconnect(); } });
    mo.observe(host, { attributes: true, attributeFilter: ['open', 'hidden'] });
    if (host !== el) mo.observe(el, { attributes: true, attributeFilter: ['hidden'] });
  });
};
watchDefer();
document.addEventListener('shopify:section:load', watchDefer);

/* ---------- desktop "Shop" panel ---------- */
const setupMega = () => {
  const btn = $('[data-mega-btn]');
  const mega = btn && document.getElementById(btn.getAttribute('aria-controls'));
  const hdr = btn && btn.closest('.hdr');
  if (!btn || !mega || !hdr || btn._gvbMega) return;
  btn._gvbMega = true;
  let timer = null;
  let hoverAt = 0;
  const hoverable = window.matchMedia('(hover:hover) and (pointer:fine)').matches;
  const setMega = (on, byHover) => {
    clearTimeout(timer);
    if (on && !desk.matches) return;
    if (on === !mega.hidden) return;
    mega.hidden = !on;
    hoverAt = on && byHover ? Date.now() : 0;
    btn.setAttribute('aria-expanded', on ? 'true' : 'false');
    hdr.classList.toggle('is-mega', on);
  };
  btn.addEventListener('click', () => {
    if (!mega.hidden && Date.now() - hoverAt < 700) { hoverAt = 0; return; }
    setMega(mega.hidden);
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !mega.hidden) { setMega(false); btn.focus(); }
  });
  document.addEventListener('click', (e) => {
    if (!mega.hidden && e.target instanceof Element && !e.target.closest('[data-mega],[data-mega-btn]')) setMega(false);
  });
  hdr.addEventListener('focusout', (e) => {
    if (!mega.hidden && e.relatedTarget instanceof Element && !e.relatedTarget.closest('[data-mega],[data-mega-btn]')) setMega(false);
  });
  /* a link inside the panel that stays on this page (an anchor) closes it */
  mega.addEventListener('click', (e) => { if (e.target instanceof Element && e.target.closest('a[href]')) setMega(false); });
  if (hoverable) {
    [btn, mega].forEach((el) => {
      el.addEventListener('mouseenter', () => { clearTimeout(timer); if (mega.hidden) timer = setTimeout(() => setMega(true, true), 120); });
      el.addEventListener('mouseleave', () => { clearTimeout(timer); timer = setTimeout(() => setMega(false), 220); });
    });
  }
  desk.addEventListener('change', (m) => { if (!m.matches) setMega(false); });
  window.addEventListener('pageshow', () => setMega(false));
  (window.GVB || (window.GVB = {})).mega = setMega;
};
setupMega();
document.addEventListener('shopify:section:load', setupMega);

/* ---------- clicks ---------- */
document.addEventListener('click', async (e) => {
  const t = e.target instanceof Element ? e.target : null;
  if (!t) return;

  const pick = t.closest('[data-s-pick]');
  if (pick && pick.closest('#search-drawer')) {
    if (runSearch(pick.getAttribute('data-s-pick'))) e.preventDefault();
    return;
  }

  const openSearch = t.closest('[data-gvb-open-search]');
  if (openSearch) {
    const sd = searchDrawer();
    if (!sd || typeof sd.show !== 'function') return; // no JS drawer: follow the link to /search
    e.preventDefault();
    const md = menuDrawer();
    if (md && md.open) await md.hide();
    sd.show();
    return;
  }

  const viewBag = t.closest('[data-gvb-view-bag]');
  if (viewBag) {
    const drawer = document.getElementById('cart-drawer') || document.querySelector('cart-drawer');
    if (drawer && typeof drawer.show === 'function') {
      e.preventDefault();
      const toast = document.getElementById('gvb-toast');
      if (toast) toast.classList.remove('is-on');
      drawer.show();
    }
    return;
  }

  /* a kit link on the kit's own page: choose the tier there (the page does not reload for a #tiers anchor) */
  const tierLink = t.closest('a[data-gvb-tier]');
  if (tierLink) {
    const url = new URL(tierLink.href, location.href);
    if (url.pathname === location.pathname && url.hash) {
      const ev = new CustomEvent('gvb:select-tier', { detail: { title: tierLink.getAttribute('data-gvb-tier') }, cancelable: true });
      document.dispatchEvent(ev);
      const target = document.querySelector('[data-tiers]') || document.getElementById(url.hash.slice(1));
      if (target) {
        e.preventDefault();
        const md = menuDrawer();
        if (md && md.open && typeof md.hide === 'function') await md.hide();
        target.scrollIntoView({ block: 'center', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        if (location.hash !== url.hash) history.replaceState(history.state, '', url.hash);
      }
      return;
    }
  }

  const menuLink = t.closest('#header-sidebar-menu a[href]');
  if (menuLink && !menuLink.hasAttribute('data-gvb-open-search')) {
    const md = menuDrawer();
    if (md && md.open && typeof md.hide === 'function') md.hide();
  }
});

/* ---------- predictive search: live count + term highlight after each swap of [slot="results"] ---------- */
const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const markTerm = (scope, term) => {
  const word = (term || '').trim().split(/\s+/)[0];
  if (!word || word.length < 2) return;
  const re = new RegExp('(' + escapeRe(word) + ')', 'i');
  scope.querySelectorAll('.s-row .nm').forEach((el) => {
    if (el.querySelector('mark')) return;
    const txt = el.textContent;
    const m = txt.match(re);
    if (!m) return;
    const i = m.index;
    el.textContent = '';
    el.append(txt.slice(0, i));
    const mk = document.createElement('mark');
    mk.textContent = txt.slice(i, i + m[0].length);
    el.append(mk, txt.slice(i + m[0].length));
  });
};
const watchSearch = () => {
  const sd = searchDrawer();
  const ps = sd && $('predictive-search', sd);
  const live = sd && $('[data-s-live]', sd);
  if (!ps || ps._gvbWatched) return;
  ps._gvbWatched = true;
  new MutationObserver(() => {
    const res = $('[slot="results"]', ps);
    if (!res) return;
    if (res.dataset.sTerm) markTerm(res, res.dataset.sTerm);
    if (live) live.textContent = res.dataset.sStatus || '';
  }).observe(ps, { childList: true });
  const form = ps.closest('form');
  if (form) form.addEventListener('reset', () => { if (live) live.textContent = ''; });
};
watchSearch();
document.addEventListener('shopify:section:load', watchSearch);
